import type { BusinessFunction } from '@lib/api-types/business-functions';
import type { DerivedContext, Skill, SkillKind } from '@lib/api-types/derived-contexts';
import type { Engineer } from '@lib/api-types/engineers';
import { IdPrefix, toPublicId } from '@lib/api-types/ids';
import type { Project } from '@lib/api-types/projects';
import type { Rating } from '@lib/api-types/ratings';
import type { Roadmap } from '@lib/api-types/roadmaps';
import type { TeamChange } from '@lib/api-types/team-changes';
import type {
  ActivityCell,
  ActivityItem,
  ActivityView,
  HistoryStatusView,
  ResilienceView,
  RoadmapView,
  TalentView,
  TimelineAxisView,
  TimelineSnapshot,
  TimelineView,
} from '@lib/api-types/views';
import {
  wrapActivityItem,
  wrapDerivedContext,
  wrapProject,
  wrapRoadmap,
  wrapTeamChange,
} from '@lib/api-types/wire-helpers';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useActiveOrg } from '../../active-org.ts';
import { useActiveRepo } from '../../active-repo.ts';
import { fetchJson, useAuth, type OrgSummary } from '../../api.ts';
import { type MatrixGrounding, useMatrixGrounding } from '../../matrix-grounding.js';
import commitAnalysisJson from '../../mock-data/commit-analysis.json' with { type: 'json' };
import derivedContextsJson from '../../mock-data/derived-contexts.json' with { type: 'json' };
import functionsJson from '../../mock-data/functions.json' with { type: 'json' };
import projectsJson from '../../mock-data/projects.json' with { type: 'json' };
import ratingsJson from '../../mock-data/ratings.json' with { type: 'json' };
import roadmapsJson from '../../mock-data/roadmaps.json' with { type: 'json' };
import teamChangesJson from '../../mock-data/team-changes.json' with { type: 'json' };
import teamJson from '../../mock-data/team.json' with { type: 'json' };


// Legacy JSON-file shapes used only by the mock-data composers below.
// The mock JSON files predate the per-entity wire surface — fields
// like `engineer`/`skill`/`rating` get translated to the new
// `engineer_id`/`skill_id`/`value` shape at compose time. Inlined
// (vs imported) because nothing else in the SPA consumes them.
interface LegacyProjectShape {
  id: string;
  display: string;
  status: string;
  origin?: string;
  prompt?: string;
  overlay?: {
    new_derived_contexts?: Array<{
      id: string;
      display: string;
      functions: string[];
      skills: Array<{ id: string; display: string; kind?: string }>;
    }>;
    new_skills_in_existing?: Array<{
      derivedContext: string;
      skill: { id: string; display: string; kind?: string };
    }>;
    new_functions?: Array<{ id: string; display: string; subtitle?: string }>;
    new_function_derived_contexts?: Record<string, string[]>;
  };
  requirements: {
    roles: Array<{ id: string; display: string }>;
    skills: Array<{ skill: string; level: 1 | 2 | 3 | 4; role: string }>;
  };
}
interface LegacyRoadmapShape {
  id: string;
  display: string;
  team_change: string | null;
  projects: string[];
}
interface LegacyTeamChangeShape {
  id: string;
  display: string;
  departures: Array<{ engineer: string }>;
  upskills: Array<{
    engineer: string;
    skill: string;
    from: 1 | 2 | 3 | 4 | null;
    to: 1 | 2 | 3 | 4;
  }>;
  hires: Array<{
    id: string;
    display: string;
    handle?: string | null;
    status: 'active' | 'departed';
    scope?: 'engineering' | 'gtm';
    kind?: 'human' | 'agent';
    ratings: Array<{ skill: string; rating: 1 | 2 | 3 | 4 }>;
  }>;
}

/**
 * Per-view hooks for the matrix surface. Each hook calls one
 * `/v1/orgs/:slug/views/*` endpoint and returns the typed payload
 * directly.
 *
 * Three states:
 *   - Signed-in with an active org → fetches the live view
 *   - Signed-out / no orgs → composes the view from bundled mock
 *     JSON so the marketing preview keeps working
 *   - `VITE_USE_MOCK_DATA=true` → force mock for Storybook / when
 *     the API is down
 */

/**
 * The resilience view, plus which BF grounding the server ACTUALLY applied.
 *
 * `appliedGrounding` is deliberately not the requested value: an org with no
 * facet rules is served `glob` no matter what the toggle says, and labelling the
 * matrix from the request would make the UI claim a model it isn't showing.
 */
export type ResilienceViewResult = ResilienceView & { appliedGrounding: MatrixGrounding };

export function useResilienceView(): UseQueryResult<ResilienceViewResult, Error> {
  const { useMock, slug, repoId, grounding } = useViewQueryState();
  return useQuery<ResilienceViewResult, Error>({
    queryKey: viewKey('resilience', useMock, slug, repoId, grounding),
    queryFn: () =>
      useMock || slug === null
        ? Promise.resolve({ ...composeMockResilienceView(), appliedGrounding: 'glob' as const })
        : fetchJson<{ view: ResilienceView; grounding?: { applied?: MatrixGrounding } }>(
            `/v1/orgs/${slug}/views/resilience${viewQuery(repoId, grounding)}`,
          ).then((r) => ({ ...r.view, appliedGrounding: r.grounding?.applied ?? 'glob' })),
    staleTime: 30_000,
    // Keep the matrix on screen while a repo / grounding switch refetches,
    // instead of dropping to the skeleton. The timeline hooks below have always
    // done this; there was never a reason the other two matrices shouldn't.
    placeholderData: (prev) => prev,
  });
}

export function useTalentView(): UseQueryResult<TalentView, Error> {
  const { useMock, slug, repoId } = useViewQueryState();
  return useQuery<TalentView, Error>({
    queryKey: viewKey('talent', useMock, slug, repoId),
    queryFn: () =>
      useMock || slug === null
        ? Promise.resolve(composeMockTalentView())
        : fetchJson<{ view: TalentView }>(
            `/v1/orgs/${slug}/views/talent${repoQuery(repoId)}`,
          ).then((r) => r.view),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useRoadmapView(): UseQueryResult<RoadmapView, Error> {
  const { useMock, slug } = useViewQueryState();
  return useQuery<RoadmapView, Error>({
    queryKey: viewKey('roadmap', useMock, slug),
    queryFn: () =>
      useMock || slug === null
        ? Promise.resolve(composeMockRoadmapView())
        : fetchJson<{ view: RoadmapView }>(
            `/v1/orgs/${slug}/views/roadmap`,
          ).then((r) => r.view),
    staleTime: 30_000,
  });
}

/**
 * Activity view page size. The SPA pre-loads the first page and
 * exposes a "See more" button that calls `fetchNextPage()` to append
 * the next page; previously-fetched pages stay cached so scrolling
 * back doesn't refetch.
 */
export const ACTIVITY_PAGE_LIMIT = 20;

export type ActivityInfiniteQueryResult = UseInfiniteQueryResult<
  { pages: ActivityView[]; pageParams: number[] },
  Error
>;

export function useActivityView(options?: { enabled?: boolean }): ActivityInfiniteQueryResult {
  const { useMock, slug } = useViewQueryState();
  return useInfiniteQuery<ActivityView, Error, { pages: ActivityView[]; pageParams: number[] }, readonly unknown[], number>({
    // The activity route is UNCACHED server-side (composes at "now" per page),
    // so callers with the feed hidden must pass enabled:false — otherwise every
    // page mount pays a full cold compose for an invisible component (perf §4.3).
    enabled: options?.enabled ?? true,
    queryKey: viewKey('activity', useMock, slug),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more ? allPages.length * ACTIVITY_PAGE_LIMIT : undefined,
    queryFn: ({ pageParam }) =>
      useMock || slug === null
        ? Promise.resolve(
            composeMockActivityPage({ offset: pageParam, limit: ACTIVITY_PAGE_LIMIT }),
          )
        : fetchJson<{ view: ActivityView }>(
            `/v1/orgs/${slug}/views/activity?limit=${ACTIVITY_PAGE_LIMIT}&offset=${pageParam}`,
          ).then((r) => r.view),
    staleTime: 30_000,
  });
}

/**
 * Optional overrides for the Timeline tick cadence + window. Omitted (the
 * default) lets the server pick the adaptive, history-length-based cadence.
 * The granularity selector supplies `stepDays` (+ `count`) to zoom to a fixed
 * interval, and `end` (unix epoch seconds) to pan the window to a date.
 */
export interface TimelineParams {
  stepDays?: number;
  count?: number;
  end?: number;
}

export function useTimelineView(
  params?: TimelineParams,
): UseQueryResult<TimelineView, Error> {
  const { useMock, slug, repoId } = useViewQueryState();
  const stepDays = params?.stepDays;
  const count = params?.count;
  const end = params?.end;
  return useQuery<TimelineView, Error>({
    // Mock key stays identical to the pre-param shape; the live key folds in
    // the cadence params so react-query refetches when the selector changes.
    queryKey: useMock
      ? (['view', 'timeline', 'mock'] as const)
      : (['view', 'timeline', slug, repoId, stepDays ?? null, count ?? null, end ?? null] as const),
    queryFn: () =>
      useMock || slug === null
        ? Promise.resolve(composeMockTimelineView())
        : fetchJson<{ view: TimelineView }>(
            `/v1/orgs/${slug}/views/timeline${timelineQueryString(repoId, params)}`,
          ).then((r) => r.view),
    staleTime: 30_000,
    // Keep the current snapshots on screen while a cadence change refetches,
    // so the toolbar + matrix don't flash the skeleton on every selector move.
    placeholderData: (prev) => prev,
  });
}

/** `?repo=…&step_days=…&count=…&end=…` — omits any absent param. */
function timelineQueryString(repoId: string | null, params?: TimelineParams): string {
  const sp = new URLSearchParams();
  if (repoId) sp.set('repo', repoId);
  if (params?.end !== undefined) sp.set('end', String(params.end));
  if (params?.stepDays !== undefined) sp.set('step_days', String(params.stepDays));
  if (params?.count !== undefined) sp.set('count', String(params.count));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * The lightweight timeline AXIS (dates + highlights) plus the newest snapshot
 * embedded for first paint. Memory-safe on the server (one composeBaseView),
 * so it works for any org size — the rest of the snapshots load lazily via
 * {@link useTimelineSnapshot}. Replaces the memory-bound `useTimelineView`.
 */
export function useTimelineAxis(
  params?: TimelineParams,
): UseQueryResult<TimelineAxisView, Error> {
  const { useMock, slug, repoId } = useViewQueryState();
  const stepDays = params?.stepDays;
  const count = params?.count;
  const end = params?.end;
  return useQuery<TimelineAxisView, Error>({
    queryKey: useMock
      ? (['view', 'timeline_axis', 'mock'] as const)
      : (['view', 'timeline_axis', slug, repoId, stepDays ?? null, count ?? null, end ?? null] as const),
    queryFn: () =>
      useMock || slug === null
        ? Promise.resolve(composeMockTimelineAxis())
        : fetchJson<{ view: TimelineAxisView }>(
            `/v1/orgs/${slug}/views/timeline/axis${timelineQueryString(repoId, params)}`,
          ).then((r) => r.view),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * ONE snapshot's full payload at `at` (an axis date). Cached per (org, at,
 * repo) — small enough for any org size. `at === null` disables the query
 * (used to skip the fetch for the newest date, which the axis already embeds).
 */
export function useTimelineSnapshot(
  at: string | null,
): UseQueryResult<TimelineSnapshot | null, Error> {
  const { useMock, slug, repoId } = useViewQueryState();
  return useQuery<TimelineSnapshot | null, Error>({
    enabled: at !== null,
    queryKey: useMock
      ? (['view', 'timeline_snapshot', 'mock', at] as const)
      : (['view', 'timeline_snapshot', slug, repoId, at] as const),
    queryFn: () => {
      if (at === null) return Promise.resolve(null);
      if (useMock || slug === null) return Promise.resolve(composeMockTimelineSnapshot(at));
      const sp = new URLSearchParams();
      sp.set('at', at);
      if (repoId) sp.set('repo', repoId);
      return fetchJson<{ snapshot: TimelineSnapshot | null }>(
        `/v1/orgs/${slug}/views/timeline/snapshot?${sp.toString()}`,
      ).then((r) => r.snapshot);
    },
    staleTime: 30_000,
    // Keep the previous date's snapshot on screen while scrubbing to an
    // uncached date, so the matrix doesn't flash empty between fetches.
    placeholderData: (prev) => prev,
  });
}

/**
 * Polls /history/status while the backfill is in flight. Mock + signed-
 * out paths short-circuit to a synthetic "done" view so the marketing
 * preview doesn't try to talk to the API.
 *
 * Side-effect: when the polled `overall_progress` transitions from <1
 * to 1, invalidate every matrix view (`['view', …]`) so newly-derived
 * ratings, highlights, and backdated structural origins land on the next
 * render (without waiting for the 30s staleTime). The server cache keys
 * on a data fingerprint, so the refetch returns freshly recomputed data.
 */
export function useHistoryStatus(): UseQueryResult<HistoryStatusView, Error> {
  const { useMock, slug } = useViewQueryState();
  const qc = useQueryClient();
  const wasInFlightRef = useRef(false);

  const query = useQuery<HistoryStatusView, Error>({
    queryKey: useMock ? (['history', 'status', 'mock'] as const) : (['history', 'status', slug] as const),
    queryFn: () =>
      useMock || slug === null
        ? Promise.resolve(emptyHistoryStatus())
        : fetchJson<{ view: HistoryStatusView }>(
            `/v1/orgs/${slug}/history/status`,
          ).then((r) => r.view),
    // Poll every 3s only while progress hasn't hit 1. Stops polling
    // automatically once `overall_progress === 1` — the query stays
    // cached, no thrash.
    refetchInterval: (q) => {
      const progress = q.state.data?.overall_progress ?? 0;
      return progress >= 1 ? false : 3_000;
    },
    staleTime: 0,
  });

  // Invalidate all matrix views on the falling edge from in-flight → done.
  useEffect(() => {
    const progress = query.data?.overall_progress ?? 0;
    const inFlight = progress < 1;
    if (wasInFlightRef.current && !inFlight) {
      void qc.invalidateQueries({ queryKey: ['view'] });
    }
    wasInFlightRef.current = inFlight;
  }, [query.data?.overall_progress, qc]);

  return query;
}

function emptyHistoryStatus(): HistoryStatusView {
  return {
    object: 'history_status',
    branch_id: 'branch_mock',
    generated_at: Math.floor(Date.now() / 1000),
    repos: [],
    org_phases: [],
    overall_progress: 1,
  };
}

// ---- Shared query state -------------------------------------------------

export function useViewQueryState(): {
  useMock: boolean;
  slug: string | null;
  repoId: string | null;
  grounding: MatrixGrounding;
} {
  const auth = useAuth();
  const orgs = auth.status === 'signed-in' ? auth.me.orgs : [];
  const { active } = useActiveOrg(orgs);
  const { activeRepoId } = useActiveRepo();
  const { grounding } = useMatrixGrounding();
  return {
    useMock: shouldUseMock(active),
    slug: active?.slug ?? null,
    repoId: activeRepoId,
    grounding,
  };
}

/**
 * Per-repo drill-down query fragment. `activeRepoId` (from the nav org
 * switcher, stored in `active-repo.ts`) is a single `repositories.id`;
 * the backend `?repo=` param takes a comma-separated list and drops
 * unknown ids, so one id passes straight through. Empty ("All repos")
 * yields no fragment — the whole-org view.
 *
 * Wired into `resilience` + `talent` + `timeline` — the view routes that
 * call `resolveRepoScopes` (see `routes/views.ts`). activity / roadmap
 * don't scope by repo server-side yet, so they intentionally omit the
 * param (and keep it out of their cache key) until that follow-up lands.
 */
export function repoQuery(repoId: string | null): string {
  return repoId ? `?repo=${encodeURIComponent(repoId)}` : '';
}

/**
 * Query string for the matrix views. `glob` is omitted rather than sent
 * explicitly, so the default request is byte-identical to the pre-toggle one —
 * every org that never touches the switch keeps hitting its existing server-side
 * cache entry.
 */
export function viewQuery(repoId: string | null, grounding: MatrixGrounding): string {
  const parts: string[] = [];
  if (repoId) parts.push(`repo=${encodeURIComponent(repoId)}`);
  if (grounding !== 'glob') parts.push(`grounding=${grounding}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * Exported so `useSnapshot` (still serving Timeline + Activity)
 * shares the same dev-override + signed-out fallback rules. Once
 * the per-view migration is complete and useSnapshot is gone, this
 * stays as the single source of "should we render mock data?".
 */
export function shouldUseMock(active: OrgSummary | null): boolean {
  if (import.meta.env.VITE_USE_MOCK_DATA === 'true') return true;
  if (active === null) return true;
  return false;
}

function viewKey(
  view: 'resilience' | 'talent' | 'roadmap' | 'activity' | 'timeline',
  useMock: boolean,
  slug: string | null,
  // Only resilience + talent pass a repoId (the two repo-scoped views).
  // `null` keeps the key identical to the pre-filter shape, so the other
  // views' cache entries are unaffected. Prefix invalidation on `['view']`
  // (see useHistoryStatus) still matches the longer key.
  repoId: string | null = null,
  // Only the matrix views pass a grounding. `'glob'` (the default) keeps the key
  // identical to its pre-toggle shape, so no existing cache entry is orphaned and
  // prefix invalidation on `['view']` still matches.
  grounding: MatrixGrounding = 'glob',
): readonly unknown[] {
  if (useMock) return ['view', view, 'mock'] as const;
  return grounding === 'glob'
    ? (['view', view, slug, repoId] as const)
    : (['view', view, slug, repoId, grounding] as const);
}

// ---- Mock timeline (uses bundled history archives) ----------------------
//
// Glob-load `mock-data/history/<date>/{derivedContexts,functions}.json`
// at build time. Each date becomes one TimelineSnapshot. Engineers +
// ratings are reused from the current mock team/ratings (the
// archives only carry comp/fn structure).

type HistoryFile = { derivedContexts: Array<Record<string, unknown>> } | { functions: Array<Record<string, unknown>> };

const historyMockModules = import.meta.glob<{ default: HistoryFile }>(
  '../../mock-data/history/*/*.json',
  { eager: true },
);

// ---- Mock composition ---------------------------------------------------
//
// The mock JSON files are the legacy snapshot shapes (snake_case
// inside the wrapper object, legacy field names like `engineer.display`
// instead of `display_name`). The composers below translate them
// into the per-view typed payloads. Field-rename rules:
//
//   Engineer.display          → display_name
//   Engineer.handle           → github_logins[]
//   Engineer.email            → primary_email
//   Engineer.scope === 'gtm'  → 'other' (legacy lumped non-eng roles)
//
//   Rating.engineer / .skill / .rating → engineer_id / skill_id / value
//
//   DerivedContext.functions (string[])  → carried through
//   DerivedContext.skills[].kind?        → defaulted to 'library' when
//                                       absent (legacy mock allows it)
//
// `created_at` / `updated_at` are set to now for the mock; the SPA
// doesn't compare them.

function composeMockResilienceView(): ResilienceView {
  const now = mockNow();
  return {
    object: 'resilience_view',
    branch_id: 'branch_mock',
    generated_at: now,
    derivedContexts: derivedContextsJson.derivedContexts.map((c) =>
      toMockDerivedContext(c, now),
    ),
    business_functions: functionsJson.functions.map((f) =>
      toMockBusinessFunction(f, now),
    ),
    engineers: teamJson.team.map((e) => toMockEngineer(e, now)),
    ratings: ratingsJson.ratings.map((r) => toMockRating(r, now)),
    ownership: [],
    introductions: [],
  };
}

function composeMockTalentView(): TalentView {
  const now = mockNow();
  return {
    object: 'talent_view',
    branch_id: 'branch_mock',
    generated_at: now,
    derivedContexts: derivedContextsJson.derivedContexts.map((c) =>
      toMockDerivedContext(c, now),
    ),
    business_functions: functionsJson.functions.map((f) =>
      toMockBusinessFunction(f, now),
    ),
    engineers: teamJson.team.map((e) => toMockEngineer(e, now)),
    ratings: ratingsJson.ratings.map((r) => toMockRating(r, now)),
    ownership: [],
    introductions: [],
  };
}

/**
 * Mock-data Activity composer with the same pagination contract as
 * the real /views/activity endpoint. Multiplies the bundled commit
 * fixtures by 4 (with per-copy SHA suffixes) so a See-more click has
 * something to reveal in the signed-out marketing preview.
 */
function composeMockActivityPage({
  offset,
  limit,
}: {
  offset: number;
  limit: number;
}): ActivityView {
  const now = mockNow();
  // commit-analysis.json wraps items under either `commits` (commit-
  // source runs) or `items` (PR-source runs). Activity view today
  // only emits `kind: 'commit'`, so PR-shaped rows get translated
  // into commit-shaped placeholders (the mock fixture sometimes
  // mixes them).
  const analysis = commitAnalysisJson as unknown as {
    commits?: LegacyActivityItem[];
    items?: LegacyActivityItem[];
  };
  const baseItems = analysis.commits ?? analysis.items ?? [];

  // Inflate to ≥40 items so paging is demoable. Each copy gets a
  // distinct SHA suffix and authored_at offset so they render as
  // separate rows in time-descending order.
  const COPIES = 4;
  const allLegacy: LegacyActivityItem[] = [];
  for (let copy = 0; copy < COPIES; copy++) {
    for (const item of baseItems) {
      allLegacy.push({
        ...item,
        sha: `${item.sha}-mock${copy}`,
      });
    }
  }

  const slice = allLegacy.slice(offset, offset + limit);
  return {
    object: 'activity_view',
    branch_id: 'branch_mock',
    generated_at: now,
    derivedContexts: derivedContextsJson.derivedContexts.map((c) =>
      toMockDerivedContext(c, now),
    ),
    business_functions: functionsJson.functions.map((f) =>
      toMockBusinessFunction(f, now),
    ),
    items: slice.map(toMockActivityItem),
    has_more: offset + limit < allLegacy.length,
  };
}

function composeMockTimelineView(): TimelineView {
  const now = mockNow();
  // Bucket history files by date.
  const byDate: Record<
    string,
    { derivedContexts?: LegacyDerivedContext[]; functions?: LegacyFunction[] }
  > = {};
  for (const [path, mod] of Object.entries(historyMockModules)) {
    const match = path.match(/\/history\/([^/]+)\/(derived-contexts|functions)\.json$/);
    if (!match) continue;
    const date = match[1]!;
    const kind = match[2]!;
    const bucket = byDate[date] ?? (byDate[date] = {});
    const file = mod.default as { derivedContexts?: unknown[]; functions?: unknown[] };
    // `kind` is the FILENAME the regex captured — `derived-contexts`, hyphenated.
    // This compared it against the camelCase field name instead, so it never
    // matched, every date was dropped by the `derivedContexts && functions`
    // filter below, and the mock timeline composed to zero snapshots — which is
    // what the signed-out marketing preview renders.
    if (kind === 'derived-contexts' && Array.isArray(file.derivedContexts)) {
      bucket.derivedContexts = file.derivedContexts as unknown as LegacyDerivedContext[];
    } else if (kind === 'functions' && Array.isArray(file.functions)) {
      bucket.functions = file.functions as unknown as LegacyFunction[];
    }
  }

  const mockEngineers = teamJson.team.map((e) => toMockEngineer(e, now));
  const mockRatings = ratingsJson.ratings.map((r) => toMockRating(r, now));

  const snapshots: TimelineSnapshot[] = Object.entries(byDate)
    .filter(([, v]) => v.derivedContexts && v.functions)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, v], idx) => ({
      date,
      derivedContexts: (v.derivedContexts ?? []).map((c) => toMockDerivedContext(c, now)),
      business_functions: (v.functions ?? []).map((f) => toMockBusinessFunction(f, now)),
      // Use the current team + ratings for every historical date — the
      // archive bundles don't carry historical ratings. Adequate for
      // the mock-data preview; real /views/timeline returns per-date
      // bitemporal ratings.
      engineers: mockEngineers,
      ratings: mockRatings,
      // Sprinkle a few deterministic highlights so the marketing
      // preview actually shows the scrubber's tick marks. Real
      // /views/timeline returns server-computed ones.
      highlight: mockHighlightFor(idx, date),
    }));

  return {
    object: 'timeline_view',
    branch_id: 'branch_mock',
    generated_at: now,
    snapshots,
  };
}

/** Mock axis: derive from the full mock timeline (dates + highlights + newest). */
function composeMockTimelineAxis(): TimelineAxisView {
  const full = composeMockTimelineView();
  const snapshots = full.snapshots;
  return {
    object: 'timeline_axis_view',
    branch_id: full.branch_id,
    generated_at: full.generated_at,
    axis: snapshots.map((s) => ({ date: s.date, highlight: s.highlight ?? null })),
    latest: snapshots[snapshots.length - 1] ?? null,
  };
}

/** Mock single snapshot: the mock timeline entry whose date matches `at`. */
function composeMockTimelineSnapshot(at: string): TimelineSnapshot | null {
  return composeMockTimelineView().snapshots.find((s) => s.date === at) ?? null;
}

function mockHighlightFor(
  idx: number,
  date: string,
): import('@lib/api-types/views').TimelineHighlight | null {
  // Index 1 → first_appearance; every 3rd thereafter → churn_burst.
  if (idx === 1) {
    return {
      kind: 'first_appearance',
      title: 'Mobile context first appears',
      summary: 'Earliest classified commit touching the Mobile context.',
      score: 1,
    };
  }
  if (idx > 1 && idx % 3 === 0) {
    return {
      kind: 'churn_burst',
      title: `Heavy push (${(idx % 5) + 3} commits)`,
      summary: `${(idx * 47) % 800 + 100} lines changed across ${(idx % 3) + 1} areas on ${date}.`,
      score: 0.6 + (idx % 4) * 0.1,
    };
  }
  return null;
}

function composeMockRoadmapView(): RoadmapView {
  const now = mockNow();
  return {
    object: 'roadmap_view',
    branch_id: 'branch_mock',
    generated_at: now,
    derivedContexts: derivedContextsJson.derivedContexts.map((c) =>
      toMockDerivedContext(c, now),
    ),
    business_functions: functionsJson.functions.map((f) =>
      toMockBusinessFunction(f, now),
    ),
    engineers: teamJson.team.map((e) => toMockEngineer(e, now)),
    ratings: ratingsJson.ratings.map((r) => toMockRating(r, now)),
    // Mock demo data continues to populate the legacy projects /
    // team_changes wire fields so the marketing preview keeps
    // rendering. `plans` (the new saved-view surface that real backend
    // populates) starts empty for the demo — users who sign in see
    // any plans they save in the sidebar.
    plans: [],
    projects: _legacyProjects.map((p) => toMockProject(p, now)),
    roadmaps: _legacyRoadmaps.map((r) => toMockRoadmap(r, now)),
    team_changes: _legacyTeamChanges.map((tc) => toMockTeamChange(tc, now)),
  };
}

function mockNow(): number {
  return Math.floor(Date.now() / 1000);
}

// ---- Per-entity mock translators (legacy JSON → wire shape) -------------

type LegacyDerivedContext = (typeof derivedContextsJson)['derivedContexts'][number];
type LegacyFunction = (typeof functionsJson)['functions'][number];
type LegacyEngineer = (typeof teamJson)['team'][number];
type LegacyRating = (typeof ratingsJson)['ratings'][number];
// Explicit interfaces (not typeof JSON) for the Roadmap-payload mocks:
// empty arrays in projects.json infer as `never[]`, which breaks the
// field-by-field translation below.
type LegacyProject = LegacyProjectShape;
type LegacyRoadmap = LegacyRoadmapShape;
type LegacyTeamChange = LegacyTeamChangeShape;

const _legacyProjects = projectsJson.projects as unknown as LegacyProject[];
const _legacyRoadmaps = roadmapsJson.roadmaps as unknown as LegacyRoadmap[];
const _legacyTeamChanges = teamChangesJson.team_changes as unknown as LegacyTeamChange[];

// Mock composers go through the same `@lib/api-types/wire-helpers`
// the real API routes use, so the signed-out / VITE_USE_MOCK_DATA
// fallback emits byte-identical wire shape to prod. Without this
// the e2e Playwright suite renders against raw-id mocks while prod
// serves prefixed-id payloads — the producer/consumer contract goes
// untested and bug classes like "matrix all grey for clowder-coffee"
// (PR #119) ship undetected.
function toMockDerivedContext(c: LegacyDerivedContext, ts: number): DerivedContext {
  return wrapDerivedContext({
    object: 'derived_context' as const,
    id: c.id,
    display: c.display,
    subtitle: null,
    skills: c.skills.map(toMockSkill),
    functions: [...c.functions],
    created_at: ts,
    updated_at: ts,
  });
}

function toMockSkill(s: LegacyDerivedContext['skills'][number]): Skill {
  return {
    id: s.id,
    display: s.display,
    kind: (s.kind ?? 'library') as SkillKind,
  };
}

function toMockBusinessFunction(f: LegacyFunction, ts: number): BusinessFunction {
  return {
    object: 'business_function',
    id: toPublicId(IdPrefix.business_function, f.id),
    display: f.display,
    ...(f.subtitle != null ? { subtitle: f.subtitle } : {}),
    created_at: ts,
    updated_at: ts,
  };
}

function toMockEngineer(e: LegacyEngineer, ts: number): Engineer {
  const scope: Engineer['scope'] =
    e.scope === 'gtm' ? 'other' : e.scope === 'engineering' ? 'engineering' : 'engineering';
  const kind: Engineer['kind'] = e.kind === 'agent' ? 'agent' : 'human';
  return {
    object: 'engineer',
    id: toPublicId(IdPrefix.engineer, e.id),
    display_name: e.display,
    primary_email: e.email ?? null,
    status: e.status === 'departed' ? 'departed' : 'active',
    scope,
    kind,
    git_emails: [],
    github_logins: e.handle ? [e.handle] : [],
    created_at: ts,
    updated_at: ts,
  };
}

function toMockRating(r: LegacyRating, ts: number): Rating {
  return {
    object: 'rating',
    // engineer_id is wrapped to match `engineer.id`; skill_id stays
    // raw because there's no SKILL public-id namespace today (the
    // routes don't wrap it either — see `repositories/ratings.ts`).
    engineer_id: toPublicId(IdPrefix.engineer, r.engineer),
    skill_id: r.skill,
    value: r.rating as Rating['value'],
    note: null,
    created_at: ts,
    updated_at: ts,
  };
}

// Legacy ActivityItem shape covers both commits and PRs with a mix
// of overlapping optional fields (subject/title, authorDate/mergedAt,
// authorName/author, sha/number). Collapse to the new wire shape
// (commits only — PRs deferred until code_pull_requests lands).
type LegacyActivityItem = {
  kind?: 'commit' | 'pr';
  sha?: string;
  shaShort?: string;
  subject?: string;
  number?: number;
  title?: string;
  authorName?: string | null;
  author?: string | null;
  authorDate?: string;
  mergedAt?: string;
  additions?: number;
  deletions?: number;
  cells?: Array<{ derivedContext: string; function: string; value: number; intensity: number }>;
};

function toMockActivityItem(raw: LegacyActivityItem): ActivityItem {
  const sha = raw.sha ?? `mock-${raw.number ?? Math.random().toString(36).slice(2, 9)}`;
  const dateIso = raw.authorDate ?? raw.mergedAt;
  const epoch = dateIso ? Math.floor(Date.parse(dateIso) / 1000) : mockNow();
  const cells: ActivityCell[] = (raw.cells ?? []).map((c) => ({
    derivedContext: c.derivedContext,
    function: c.function,
    value: c.value,
    intensity: c.intensity,
  }));
  // wrapActivityItem prefixes each cell's derivedContext + function refs
  // so the heatmap join matches the prefixed comp / fn ids alongside.
  return wrapActivityItem({
    object: 'activity_item',
    kind: 'commit', // wire shape only supports commit for now
    sha,
    sha_short: raw.shaShort ?? sha.slice(0, 7),
    subject: raw.subject ?? raw.title ?? '(no subject)',
    author_login: raw.author ?? null,
    author_name: raw.authorName ?? null,
    authored_at: epoch,
    committed_at: epoch,
    additions: raw.additions ?? 0,
    deletions: raw.deletions ?? 0,
    files_total: 0,
    files_unmapped: 0,
    files_skipped: 0,
    cells,
    commit_url: null,
  });
}

// Legacy mock project statuses are a wider set (`draft | active |
// shipped | paused`) than the per-entity type allows
// (`draft | committed | in-progress`). Map active→in-progress,
// shipped/paused→committed; leave draft alone.
function mapLegacyProjectStatus(s: string): Project['status'] {
  switch (s) {
    case 'active':
      return 'in-progress';
    case 'shipped':
    case 'paused':
      return 'committed';
    case 'in-progress':
    case 'committed':
    case 'draft':
      return s;
    default:
      return 'draft';
  }
}

function toMockProject(p: LegacyProject, ts: number): Project {
  return wrapProject({
    object: 'project',
    id: p.id,
    display: p.display,
    status: mapLegacyProjectStatus(p.status),
    origin: (p.origin as Project['origin']) ?? 'scratch',
    prompt: p.prompt ?? null,
    impact: { cells: [] },
    overlay: {
      new_derived_contexts:
        p.overlay?.new_derived_contexts?.map((nc) => ({
          id: nc.id,
          display: nc.display,
          functions: [...nc.functions],
          skills: nc.skills.map((s) => ({
            id: s.id,
            display: s.display,
            kind: (s.kind ?? 'library') as SkillKind,
          })),
        })) ?? [],
      new_skills_in_existing:
        p.overlay?.new_skills_in_existing?.map((ns) => ({
          derivedContext: ns.derivedContext,
          skill: {
            id: ns.skill.id,
            display: ns.skill.display,
            kind: (ns.skill.kind ?? 'library') as SkillKind,
          },
        })) ?? [],
      new_functions:
        p.overlay?.new_functions?.map((nf) => ({
          id: nf.id,
          display: nf.display,
          ...(nf.subtitle != null ? { subtitle: nf.subtitle } : {}),
        })) ?? [],
      new_function_derived_contexts: { ...(p.overlay?.new_function_derived_contexts ?? {}) },
    },
    requirements: {
      roles: p.requirements.roles.map((r) => ({ id: r.id, display: r.display })),
      skills: p.requirements.skills.map((s) => ({
        skill: s.skill,
        level: s.level,
        role: s.role,
      })),
    },
    created_at: ts,
    updated_at: ts,
  });
}

function toMockRoadmap(r: LegacyRoadmap, ts: number): Roadmap {
  return wrapRoadmap({
    object: 'roadmap',
    id: r.id,
    display: r.display,
    team_change: r.team_change,
    projects: [...r.projects],
    created_at: ts,
    updated_at: ts,
  });
}

function toMockTeamChange(tc: LegacyTeamChange, ts: number): TeamChange {
  return wrapTeamChange({
    object: 'team_change',
    id: tc.id,
    display: tc.display,
    departures: tc.departures.map((d) => ({
      engineer_id: d.engineer,
      effective: '',
    })),
    upskills: tc.upskills.map((u) => ({
      engineer_id: u.engineer,
      skill_id: u.skill,
      from: u.from as Rating['value'] | null,
      to: u.to as Rating['value'],
    })),
    hires: tc.hires.map((h) => {
      const scope: TeamChange['hires'][number]['scope'] =
        h.scope === 'gtm' ? 'other' : h.scope === 'engineering' ? 'engineering' : 'engineering';
      return {
        id: h.id,
        display: h.display,
        status: h.status === 'departed' ? ('departed' as const) : ('active' as const),
        scope,
        kind: h.kind === 'agent' ? ('agent' as const) : ('human' as const),
        ratings: h.ratings.map((rt) => ({
          skill_id: rt.skill,
          rating: rt.rating as Rating['value'],
        })),
      };
    }),
    created_at: ts,
    updated_at: ts,
  });
}
